import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import {
  setSidebarOpenTab,
  sidebarOpenTabAtom,
  SidebarTab,
} from '@/atoms/persistedPreferencesAtoms';
import { sessionPanelHostsAiSurfaceAtom } from '@heidi/session/data';

import { useIsWorkInSessionEnabled } from '@/app-shell/flags/agents-flags';
import { useIsAskHeidiSourcesShortcutsEnabled } from '@/app-shell/flags/evidence-flags';
import { IS_INSET_DESIGN_ENABLED } from '@/app-shell/flags/shell-flags';
import { ChatRenderer, useSessionResearchVerbs } from '@/components/chat/Chat';
import { SessionAskHeidiPane } from '@/components/ui/session-ask-heidi/session-ask-heidi-pane';
import { useSessionAskHeidiChatId } from '@/components/ui/session-ask-heidi/use-session-ask-heidi-chat-id';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { verbToAnalyticsVerb } from '@/models/analytics/features/evidence';
import { SessionWorkVoiceSidebarSync, type SessionResearchVerb } from '@heidi/agents';
import { Drawer, DrawerContent } from '@heidi/design-system/drawer';
import { ResizableHandle, ResizablePanel } from '@heidi/design-system/resizable';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { SESSION_PAGE_INSET_TOP_PADDING_CLASS } from '@heidi/session';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useCloseEvidenceSidebarOnLeaveSession } from './hooks/useCloseEvidenceSidebarOnLeaveSession';
import { useEvidenceSidebarPanel } from './hooks/useEvidenceSidebarPanel';

/**
 * The Work landing's Research card props for an in-session host. Both
 * {@link EvidenceSidebarPanel} and {@link EvidenceSidebarDrawer} hand these to
 * `SessionAskHeidiPane`; the verbs come from Chat, which the pane may not import.
 */
const useSessionResearchVerbProps = () => {
  // The Research card is gated by `useIsWorkInSessionEnabled()` alone and is
  // deliberately independent of the in-session Ask Heidi bar experiment
  // (`exp-evidence-in-session-capability-signalling`): the panel resolves the
  // Direction C taxonomy regardless of that experiment's arm.
  const resolvedResearchVerbs = useSessionResearchVerbs();
  // The panel is locked to the session's patient, so a row that carries a
  // triggerSuffix (`@`/`#` opens the mention menu to attach or link a
  // different patient or session) is dropped here; a verb whose rows all
  // fall away is dropped with it. Direction C ships none of these rows, so
  // the filter is a guard for any future row, not a change to today's set.
  const researchVerbs = useMemo<readonly SessionResearchVerb[]>(
    () =>
      resolvedResearchVerbs
        .map((verb) => ({
          id: verb.id,
          label: verb.label,
          icon: verb.icon,
          items: verb.items
            .filter((item) => !item.triggerSuffix)
            .map((item) => ({ ...item })),
        }))
        .filter((verb) => verb.items.length > 0),
    [resolvedResearchVerbs]
  );
  const onOpenResearchVerb = useCallback((verbId: string) => {
    const analyticsVerb = verbToAnalyticsVerb[verbId];
    if (!analyticsVerb) return;

    track({
      options: { amplitude: false },
      event_name: TrackingEventName.EvidenceVerbClicked,
      props: { verb: analyticsVerb, is_expanded: true },
    });
  }, []);

  return { researchVerbs, onOpenResearchVerb };
};

type EvidenceSidebarBodyProps = {
  sessionId: string;
  /** Already resolved by the host, because selecting it has a side effect. */
  chatId?: string | null;
  onClose: () => void;
};

/**
 * The AI surface itself — Work or Ask Heidi — without any of the panel chrome
 * around it.
 *
 * Split out so the V2 shell's session-panel column can host the same body
 * without a second copy of the Work-vs-Ask-Heidi rule. That rule is the reason
 * this is a component rather than a snippet: if the two hosts drifted, the same
 * session would show a different AI surface depending only on how wide the
 * window was, and a routine-run deep link would land on Ask Heidi in one and
 * Work in the other.
 *
 * `chatId` arrives as a prop rather than being read here because resolving it
 * auto-selects the session's most recent chat — a side effect each host has to
 * be able to place for itself, above or below its own mount gate.
 */
export const EvidenceSidebarBody = ({
  sessionId,
  chatId,
  onClose,
}: EvidenceSidebarBodyProps) => {
  const { researchVerbs, onOpenResearchVerb } = useSessionResearchVerbProps();
  // Bring the side-panel Sources button to parity with the floating Ask Heidi
  // bar (GRO-2897): same flag gate so both surfaces show the personalised
  // avatar/scale-chip treatment together.
  const personalisedSourceLogos = useIsAskHeidiSourcesShortcutsEnabled();

  return (
    <SessionAskHeidiPane
      sessionId={sessionId}
      sidebarChatId={chatId ?? null}
      onClose={onClose}
      showRoutineRunWithoutWork
      researchVerbs={researchVerbs}
      onOpenResearchVerb={onOpenResearchVerb}
      renderEvidenceChat={(chatProps) => (
        <ChatRenderer
          {...chatProps}
          sessionId={sessionId}
          onClose={onClose}
          enableOnboardingTooltip={false}
          enableSourcesSpotlight
          personalisedSourceLogos={personalisedSourceLogos}
          analyticsSurface="side_panel"
        />
      )}
    />
  );
};

/**
 * Right-side resizable Evidence/Ask Heidi / Work-in-session panel.
 * Rendered only when IS_EVIDENCE_SIDEBAR_CHAT_EXPAND_ENABLED is ON + in-session.
 *
 * Work owns any Voice↔Work slot swap ({@link SessionWorkPanel}); this shell
 * only chooses Ask Heidi vs Work. Voice sidebar sync mounts only when the
 * body can host Work (Ask Heidi access + session) so Voice cannot open an
 * empty RHS. Note column stays. Sessions-list coordination in
 * `useSessionsPanelControls`.
 */
type EvidenceSidebarPanelProps = {
  /** DOM id of the immediate resizable group used for pixel-based constraints. */
  panelContainerId?: string;
  /**
   * Sessions split workspace only: the column sits directly under the desktop
   * title bar, so its top inset collapses like the sibling list/detail
   * columns. The legacy `/scribe/session/*` route keeps the full inset
   * because there the panel aligns with `PageShell.Main`'s `sm:m-3` card
   * margin.
   */
  collapseTopInsetUnderTitlebar?: boolean;
};

export const EvidenceSidebarPanel = ({
  panelContainerId,
  collapseTopInsetUnderTitlebar = false,
}: EvidenceSidebarPanelProps = {}) => {
  const sessionId = useCurrentSessionId();
  const columnHostsAiSurface = useAtomValue(sessionPanelHostsAiSurfaceAtom);
  // The in-column host runs this selection. Calling it here as well would
  // double the chats query and the auto-select effect for the same session.
  const sidebarChatId = useSessionAskHeidiChatId(
    columnHostsAiSurface ? undefined : sessionId
  );
  const setEvidenceSidebarOpen = useSetAtom(evidenceSidebarOpenAtom);
  const { isInSessionEvidenceShown: isAskHeidiEnabled } = useEvidenceAccess();
  const isWorkInSessionEnabled = useIsWorkInSessionEnabled();

  const { isOpen, panelRef, panelConfig, handleResize, isDragging, handleDragging } =
    useEvidenceSidebarPanel(panelContainerId);
  useCloseEvidenceSidebarOnLeaveSession();

  const handleClose = useCallback(() => {
    setEvidenceSidebarOpen(false);
  }, [setEvidenceSidebarOpen]);

  // Sync only when the RHS body can paint Work/Voice. Otherwise Voice could
  // open an empty shell (Ask Heidi off / no session) while Chat/Work stay gated.
  const canHostWorkVoiceSlot = Boolean(
    isWorkInSessionEnabled && isAskHeidiEnabled && sessionId
  );

  return (
    <>
      {canHostWorkVoiceSlot ? (
        <SessionWorkVoiceSidebarSync setOpen={setEvidenceSidebarOpen} />
      ) : null}
      <ResizableHandle
        withHandle={isOpen}
        disabled={!isOpen}
        onDragging={handleDragging}
        className={cn(
          'transition-all duration-150 ease-out',
          isOpen
            ? 'group hover:bg-border-hover [&>div]:opacity-0 [&>div]:transition-opacity [&>div]:hover:opacity-100'
            : 'pointer-events-none w-0 border-0 opacity-0',
          IS_INSET_DESIGN_ENABLED() && 'bg-transparent'
        )}
      />
      <ResizablePanel
        ref={panelRef}
        id="evidence-sidebar-panel"
        order={2}
        defaultSize={isOpen ? panelConfig.defaultSize : 0}
        minSize={isOpen ? panelConfig.minSize : 0}
        maxSize={isOpen ? panelConfig.maxSize : 0}
        collapsible
        collapsedSize={0}
        onResize={handleResize}
        className={cn(
          'flex h-full max-h-full max-w-4xl flex-row overflow-hidden',
          // Scope to `flex` (shorthand → animates `flex-grow`/`flex-shrink`/`flex-basis`,
          // which is what react-resizable-panels writes inline). Without it, `duration-500`
          // falls back to `transition: all` and the inset padding toggle below
          // (`sm:pb-3 sm:pe-3` plus the titlebar-aware top inset, or `sm:pt-3`
          // on the legacy route) interpolates alongside the size — looks like
          // a scale on top of the slide-in.
          // Don't expand to `transition-[flex-grow,…]`: `eslint-plugin-tailwindcss`
          // (`migration-from-tailwind-2`) rewrites `flex-grow` → `grow` inside arbitrary
          // values on `--fix`, silently producing invalid CSS and killing the animation.
          !isDragging && 'transition-[flex] duration-500 ease-out',
          isOpen && IS_INSET_DESIGN_ENABLED() && 'sm:pb-3 sm:pe-3',
          isOpen &&
            IS_INSET_DESIGN_ENABLED() &&
            (collapseTopInsetUnderTitlebar
              ? SESSION_PAGE_INSET_TOP_PADDING_CLASS
              : 'sm:pt-3')
        )}
      >
        {isAskHeidiEnabled && sessionId && isOpen && (
          <aside
            className={cn(
              'hidden size-full flex-col gap-2 overflow-hidden @container/evidence-panel md:flex',
              IS_INSET_DESIGN_ENABLED() && 'sm:rounded-lg'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* `rounded-[inherit]` clips the panel content (e.g. the Work
                panel's full-bleed `bg-surface-shell`) to the same radius the
                inset `aside` rounds to — otherwise a square child background
                bleeds past the rounded, overflow-hidden ancestor at the bottom
                corners. No-op where the host radius is 0. */}
            <div className="relative size-full overflow-hidden rounded-[inherit]">
              <EvidenceSidebarBody
                sessionId={sessionId}
                chatId={sidebarChatId}
                onClose={handleClose}
              />
            </div>
          </aside>
        )}
      </ResizablePanel>
    </>
  );
};

type EvidenceSidebarDrawerProps = {
  sessionId: string;
  /**
   * True when this drawer is the surface elected to paint Ask Heidi — below
   * `md`, or on `/scribe/sessions` whenever the split cannot fit (the desktop
   * panel only mounts inside the split workspace, EVI-2119), and never inside
   * the Medical Director widget or without in-session Evidence access. The
   * session page derives it from `useAskHeidiSurface` and passes it in.
   */
  isActiveSurface: boolean;
};

/**
 * Full-height Ask Heidi drawer — {@link EvidenceSidebarPanel}'s counterpart
 * where the panel cannot paint, rendering the same `SessionAskHeidiPane` so
 * every width makes one Work/Evidence call. Mounted by the session page.
 */
export const EvidenceSidebarDrawer = ({
  sessionId,
  isActiveSurface,
}: EvidenceSidebarDrawerProps) => {
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const evidenceSidebarOpen = useAtomValue(evidenceSidebarOpenAtom);
  const setEvidenceSidebarOpen = useSetAtom(evidenceSidebarOpenAtom);

  const sidebarChatId = useSessionAskHeidiChatId(sessionId);
  const { researchVerbs, onOpenResearchVerb } = useSessionResearchVerbProps();

  const isAskHeidiSidebarOpen =
    evidenceSidebarOpen || sidebarOpenTab === SidebarTab.ASK_HEIDI_V2;

  const handleClose = useCallback(() => {
    setSidebarOpenTab(undefined);
    setEvidenceSidebarOpen(false);
  }, [setEvidenceSidebarOpen]);

  if (!isActiveSurface) {
    return null;
  }

  return (
    <Drawer
      open={isAskHeidiSidebarOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      shouldScaleBackground={false}
    >
      <DrawerContent
        hideCloseIcon
        overlayClassName="z-40"
        className="z-40 !mt-0 !h-screen !max-h-screen !rounded-none border-0 p-0"
      >
        <div className="size-full">
          <SessionAskHeidiPane
            sessionId={sessionId}
            sidebarChatId={sidebarChatId}
            onClose={handleClose}
            onOpenInSessionView={handleClose}
            showRoutineRunWithoutWork
            researchVerbs={researchVerbs}
            onOpenResearchVerb={onOpenResearchVerb}
            renderEvidenceChat={(chatProps) => (
              <ChatRenderer
                {...chatProps}
                sessionId={sessionId}
                onClose={handleClose}
                showMobileMenu={true}
              />
            )}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};
